import React, { useCallback } from 'react'

import { Icon, Text, Tile, T, VStack, HStack } from 'govwell-ui'
import { twMerge } from 'tailwind-merge'
import { AICommunityAssistantUsageTier } from 'types/graphql'

type Props = {
  tier: AICommunityAssistantUsageTier
  isDisabled: boolean
  handleNotifySalesTeam: (tier: AICommunityAssistantUsageTier) => Promise<void>
}

const AIAssistantUpsellTier = ({ tier, isDisabled, handleNotifySalesTeam }: Props) => {
  const handleClick = useCallback(async () => {
    if (isDisabled) {
      return
    }
    await handleNotifySalesTeam(tier)
  }, [isDisabled, tier, handleNotifySalesTeam])
  const isCurrent = tier.isCurrent
  return (
    <Tile
      key={tier.id}
      className={twMerge(
        'flex-1',
        isDisabled && 'cursor-not-allowed',
        isCurrent && 'border-2 border-yellow-500 bg-yellow-50',
        isDisabled && !isCurrent && 'opacity-50'
      )}
      aria-disabled={isDisabled}
      onClick={handleClick}
    >
      <VStack align="start" gap={4}>
        <HStack justify="space-between" gap={2}>
          <Text className="text-xl font-semibold">{tier.name}</Text>
          {isCurrent && (
            <span className="rounded-full bg-yellow-500 px-2 py-0.5 text-xs font-semibold text-white">
              CURRENT
            </span>
          )}
          {tier.isPopular && !isDisabled && (
            <span className="rounded-full bg-neutral-200 px-2 py-0.5 text-xs font-semibold text-neutral-700">
              POPULAR
            </span>
          )}
        </HStack>
        <HStack gap={2}>
          <T.BodyLarge className="text-3xl font-semibold whitespace-nowrap">
            {tier.priceText}
          </T.BodyLarge>
          {!!tier.priceDetails?.length && (
            <VStack align="start">
              {tier.priceDetails.map((detail, i) => (
                <T.Body key={i} className="text-neutral-600">
                  {detail}
                </T.Body>
              ))}
            </VStack>
          )}
        </HStack>
        <HStack align="center" gap={2}>
          <Icon.FaComments className="text-neutral-600" />
          <T.Body className="text-neutral-600">{tier.allowedResponsesText}</T.Body>
        </HStack>
        {!isDisabled && (
          <T.Body
            className={twMerge(
              'rounded-md border border-neutral-300 px-4 py-1',
              tier.buttonType === 'primary' && 'border-orange-600 bg-orange-600 text-white',
              tier.buttonType === 'default' && 'border-neutral-300'
            )}
          >
            {tier.buttonText}
          </T.Body>
        )}
      </VStack>
    </Tile>
  )
}

export default React.memo(AIAssistantUpsellTier)
